/**
 * Node fields kept on this device only, for maps in schema v2
 * (map-format.js LOCAL_FIELDS): folding a branch does not fold it for others
 * (decision 1 of docs/plans/2026-10-02-crdt-schema-v2.md), and measured
 * sizes depend on this device's fonts.
 *
 * `collapsed` is saved in the device's preferences (`orbital-collapsed-<docId>`,
 * removed with the other `orbital-` preferences at sign-out); sizes stay in
 * memory and are measured again after a reload. A node with no value here
 * shows what the map held before it moved to v2 (YjsDocManager reads it).
 */
import { getPref, setPref } from "../storage/index.js";

const COLLAPSED_PREFIX = "orbital-collapsed-";

export class LocalNodeState {
  /**
   * @param {string} docId
   * @param {{persist?: boolean}} [options] - persist: false for copies that
   *   are not the user's open map (tests, readers, recovery)
   */
  constructor(docId, { persist = true } = {}) {
    this.docId = docId;
    this.persist = persist && !!docId;
    this.values = new Map(); // nodeId → { collapsed?, mmWidth?, mmHeight?, mmTextHash? }
    this._loaded = false;
    this._saveQueued = false;
  }

  _load() {
    if (this._loaded) return;
    this._loaded = true;
    if (!this.persist) return;
    try {
      const raw = getPref(COLLAPSED_PREFIX + this.docId);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved && typeof saved === "object") {
        for (const [id, collapsed] of Object.entries(saved)) this.values.set(id, { collapsed: !!collapsed });
      }
    } catch (err) {
      console.warn("[LocalNodeState] Could not read the folded branches of", this.docId, err);
    }
  }

  /** This device's fields of a node, or null. */
  get(nodeId) {
    this._load();
    return this.values.get(nodeId) || null;
  }

  set(nodeId, fields) {
    this._load();
    this.values.set(nodeId, { ...(this.values.get(nodeId) || {}), ...fields });
    if ("collapsed" in fields) this._queueSave();
  }

  /** One save for all the changes of this task (folding a whole branch is one per node). */
  _queueSave() {
    if (!this.persist || this._saveQueued) return;
    this._saveQueued = true;
    queueMicrotask(() => {
      this._saveQueued = false;
      const saved = {};
      for (const [id, fields] of this.values) {
        if (fields.collapsed !== undefined && fields.collapsed !== null) saved[id] = fields.collapsed ? 1 : 0;
      }
      try {
        setPref(COLLAPSED_PREFIX + this.docId, JSON.stringify(saved));
      } catch (err) {
        console.warn("[LocalNodeState] Could not save the folded branches of", this.docId, err);
      }
    });
  }
}
